/**
 * wallpaperThumb — request a small thumbnail variant of a wallpaper image URL
 * for the picker grid, SAFELY.
 *
 * THE PROBLEM
 * -----------
 * The wallpaper picker (Preset grid) renders one <img> per preset at ~40px tall,
 * but each <img> downloads the FULL-SIZE source image. With dozens of presets
 * that is a lot of unnecessary bytes — the "wallpapers load slowly" complaint.
 *
 * THE SAFE FIX
 * ------------
 * If the image is served by an on-the-fly resizing CDN (imgix-style — the same
 * `?w=...&fit=crop&auto=format` convention already used in utils/helpers.js
 * srcset()), we can ask for a tiny variant and cut the payload dramatically.
 *
 * BUT the wallpaper presets come from `/wheel/get-storage-images/` (S3). A raw
 * S3 object URL does NOT resize on query params — appending `?w=96` to it would
 * download the full image anyway, and could corrupt a pre-signed URL that
 * already carries query params (signature mismatch -> 403). So we ONLY rewrite
 * when the host is a known resizing CDN; otherwise we return the URL untouched.
 *
 * This makes the change behavior-preserving today (S3 -> unchanged, still works)
 * AND forward-compatible: the day media moves behind an imgix/Cloudinary-style
 * CDN (see the AWS "media not behind CloudFront" gap flagged for the dev), the
 * picker automatically starts serving thumbnails with no further code change.
 *
 * @param {string} url   original image URL
 * @param {number} width target thumbnail width in CSS px (default 96)
 * @returns {string}     a thumbnail URL when safe, else the original url
 */

// Hosts known to honor imgix-style resize query params. Extend this list when a
// resizing CDN is put in front of the media bucket.
const RESIZING_CDN_HOSTS = [
  "imgix.net",
  "imgix.com",
];

export function wallpaperThumb(url, width = 96) {
  if (!url || typeof url !== "string") return url;

  // Never touch data URIs, gradients, or anything that isn't an http(s) image.
  if (!/^https?:\/\//i.test(url)) return url;

  let parsed;
  try {
    parsed = new URL(url);
  } catch (_e) {
    return url; // unparseable -> leave it alone
  }

  const host = parsed.hostname.toLowerCase();
  const isResizingCdn = RESIZING_CDN_HOSTS.some(
    (h) => host === h || host.endsWith(`.${h}`),
  );

  // Only safe to request a resized variant on a CDN that supports it.
  if (!isResizingCdn) return url;

  // Don't clobber an existing width directive (e.g. a pre-built srcset URL).
  if (parsed.searchParams.has("w")) return url;

  parsed.searchParams.set("w", String(Math.max(1, Math.round(width))));
  parsed.searchParams.set("fit", "crop");
  parsed.searchParams.set("auto", "format");
  return parsed.toString();
}

export default wallpaperThumb;
